import { User } from '@/types';
import { apiClient } from './api';

/**
 * Auth service for ZewstID integration.
 * Tokens come from ZewstID (external auth provider).
 * This service manages token storage and API header injection.
 */
class AuthService {
  private static instance: AuthService;
  private user: User | null = null;
  private token: string | null = null;
  private requestInterceptorId: number | null = null;
  private responseInterceptorId: number | null = null;

  private constructor() {
    this.loadFromStorage();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private loadFromStorage() {
    const storedToken = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');

    if (storedToken) {
      this.token = storedToken;
      this.setupInterceptor();
    }
    if (storedUser) {
      try {
        this.user = JSON.parse(storedUser);
      } catch {
        localStorage.removeItem('user');
      }
    }
  }

  private setupInterceptor() {
    const client = apiClient.getClient();

    // Eject existing interceptors to prevent duplicates
    if (this.requestInterceptorId !== null) {
      client.interceptors.request.eject(this.requestInterceptorId);
    }
    if (this.responseInterceptorId !== null) {
      client.interceptors.response.eject(this.responseInterceptorId);
    }

    this.requestInterceptorId = client.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Handle 401 by redirecting to login — but avoid redirect loops
    this.responseInterceptorId = client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Don't redirect if we're already on the login page or if this was the /auth/me check
          const url = error.config?.url || '';
          const isAuthEndpoint = url.includes('/auth/');
          const isLoginPage = window.location.pathname.startsWith('/login');
          if (!isAuthEndpoint && !isLoginPage) {
            this.logout();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Set token from ZewstID callback/redirect.
   * Called after the user authenticates with ZewstID.
   */
  async setToken(token: string): Promise<User> {
    this.token = token;
    localStorage.setItem('access_token', token);
    this.setupInterceptor();

    // Fetch user profile from our backend
    const user = await apiClient.getCurrentUser();
    this.user = user;
    localStorage.setItem('user', JSON.stringify(user));

    return user;
  }

  /**
   * For development/testing: login with a token directly.
   * In production, ZewstID handles authentication.
   */
  async loginWithToken(token: string): Promise<User> {
    return this.setToken(token);
  }

  /**
   * Get ZewstID login URL.
   * Returns the URL to redirect to for authentication.
   */
  getLoginUrl(): string {
    const zewstIdUrl = import.meta.env.VITE_ZEWSTID_URL || 'https://id.zewst.com';
    const clientId = import.meta.env.VITE_ZEWSTID_CLIENT_ID || 'zewst-app';
    const redirectUri = encodeURIComponent(window.location.origin + '/login/callback');
    return `${zewstIdUrl}/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=openid profile email`;
  }

  logout() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  getUser(): User | null {
    return this.user;
  }

  getToken(): string | null {
    return this.token;
  }

  hasPermission(permission: string): boolean {
    // Read the freshest user from storage in case role changed externally
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? (() => { try { return JSON.parse(storedUser) as User; } catch { return this.user; } })() : this.user;
    if (!user) return false;

    if (user.role === 'OWNER' || user.role === 'ADMIN') return true;

    const permissions: Record<string, string[]> = {
      USER: ['create', 'edit', 'deploy', 'view'],
      VIEWER: ['view'],
    };

    return permissions[user.role]?.includes(permission) || false;
  }
}

export const authService = AuthService.getInstance();
